<template>
  <div class="container public">
    <page-header />
    <div class="content-container">
      <div class="<overview-list-section">
        <h2 class="section-title">{{ $t('cdcDevicePage.title') }} <span v-if="organizationForFacility" class="blue-text"> {{ selectedFacility.name }} / {{ organizationForFacility.code }} </span></h2>
        <div class="btn-toolbar">
          <button type="button" class="btn btn-info mr-3" @click="backToFacilities">
            <font-awesome-icon icon="arrow-alt-circle-left" /> &nbsp; {{ $t('cdcDevicePage.backToFacilities') }}
          </button>
         <button v-if="userMayEdit" type="button" class="btn btn-primary mr-3" @click="showDetail('add')">
           <font-awesome-icon icon="plus" /> &nbsp; {{ $t('cdcDevicePage.addButton') }}
         </button>
         <button  v-if="userMayEdit" type="button" class="btn btn-secondary  mr-3" @click="showDetail('edit')"
           :disabled="this.selectedDeviceId === -1"><font-awesome-icon icon="edit" />&nbsp; {{ $t('cdcDevicePage.editButton') }}</button>
         <button  v-if="userMayEdit" type="button" class="btn btn-secondary  mr-3" @click="askDelete"
           :disabled="this.selectedDeviceId  === -1"><font-awesome-icon icon="trash" />&nbsp; {{ $t('cdcDevicePage.deleteButton') }}</button>
         <button v-if="userMayEdit" type="button" class="btn btn-primary" @click="showAddVirtualSensor">
           <font-awesome-icon icon="plus" /> &nbsp; {{ $t('cdcDevicePage.addVirtualSensorButton') }}
         </button>
        </div>
        <error-row @clearError="clearError" :errorMessage="errorMessage" />
        <div v-show="isLoading" class="is-loading-section" >
          <button class="btn btn-primary"><span class="spinner-border spinner-border-sm"></span></button><span> {{ $t('cdcDevicePage.isLoading') }} </span>
        </div>
        <div v-show="!isLoading && hasPhysicalDevices" id="table-section" >
          <table class="table table-hover auto-col">
            <thead>
              <tr>
                <th v-if="userMayEdit">&nbsp;</th>
                <th @click="sort('enabled')">{{ $t('cdcDevicePage.columnEnabled') }}</th>
                <th @click="sort('name')">{{ $t('cdcDevicePage.columnName') }}</th>
                <th @click="sort('apikey')">{{ $t('cdcDevicePage.columnApiKey') }}</th>
                <th @click="sort('lastseen')">{{ $t('cdcDevicePage.columnLastseen') }}</th>
                <th @click="sort('serialnumber')">{{ $t('cdcDevicePage.columnSerialNumber') }}</th>
                <th @click="sort('notificationstatus')">{{ $t('cdcDevicePage.columnNotificationStatus') }}</th>
                <th @click="sort('status')">{{ $t('cdcDevicePage.columnStatus') }}</th>
                <th colspan="2" @click="sort('sensorcnt')">{{ $t('cdcDevicePage.columnSensorCnt') }}</th>
                <th colspan="2" @click="sort('datapackagecnt')">{{ $t('cdcDevicePage.columnDataPackageCnt') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(device, index) in sortedDevices"
                @click="selectRow(device)"
                :class="{'table-primary' : isDeviceSelected(device.id)}"
                :key="index"
              >
                <td v-if="userMayEdit"><span @click.stop="editDevice(device)"><font-awesome-icon icon="edit" /></span></td>
                <td class="text-center">
                  <span v-if="device.enabled">{{ $t('cdcDevicePage.booleanTrue') }}</span>
                  <span v-else>{{ $t('cdcDevicePage.booleanFalse') }}</span>
                </td>
                <td>
                  <span v-if="device.name">{{ device.name }}</span>
                  <span v-else>{{ $t('cdcDevicePage.noname') }}</span>
                </td>
                <td> {{ device.apikey }} </td>
                <td> {{ formatDate(device.lastseen) }} </td>
                <td class="text-center"> {{ device.serialnumber }} </td>
                <td class="text-center">
                  <span v-if="device.notificationstatus">{{ $t('cdcDevicePage.booleanTrue') }}</span>
                  <span v-else>{{ $t('cdcDevicePage.booleanFalse') }}</span>
                </td>
                <td class="text-center"> {{ device.status }} </td>
                <td class="text-center"><span v-if="device.sensorcnt > 0">{{ device.sensorcnt }}</span></td>
                <td><button class="btn btn-outline-info btn-sm" @click="gotoSensors(device)">{{ $t('cdcDevicePage.editSensors') }}</button></td>
                <td class="text-center"><span v-if="device.datapackagecnt > 0">{{ device.datapackagecnt }}</span></td>
                <td><button class="btn btn-outline-info btn-sm" @click="showData(device)">{{ $t('cdcDevicePage.showData') }}</button></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-show="!isLoading && virtualDevice" class="card mt-3" id="manual-meters-section">
          <div class="card-body">
            <h5 class="card-title">{{ $t('cdcDevicePage.manualMetersTitle') }}</h5>
            <p class="card-text text-muted">{{ $t('cdcDevicePage.manualMetersHint') }}</p>
            <p v-if="virtualDevice" class="card-text">
              <span class="badge text-bg-secondary">{{ $t('cdcDevicePage.manualMetersCount', { count: virtualDevice.sensorcnt || 0 }) }}</span>
            </p>
            <button v-if="virtualDevice" type="button" class="btn btn-outline-info mr-2" @click="gotoSensors(virtualDevice)">
              <font-awesome-icon icon="edit" /> &nbsp; {{ $t('cdcDevicePage.manualMetersOpen') }}
            </button>
            <button v-if="userMayEdit" type="button" class="btn btn-primary" @click="showAddVirtualSensor">
              <font-awesome-icon icon="plus" /> &nbsp; {{ $t('cdcDevicePage.addVirtualSensorButton') }}
            </button>
          </div>
        </div>
        <div v-show="!isLoading" class="card mt-3" id="computed-meters-section">
          <div class="card-body">
            <h5 class="card-title">{{ $t('cdcDevicePage.computedMetersTitle') }}</h5>
            <p class="card-text text-muted">{{ $t('cdcDevicePage.computedMetersHint') }}</p>
            <table v-if="computedSensors.length > 0" class="table table-hover auto-col">
              <thead>
                <tr>
                  <th v-if="userMayEdit">&nbsp;</th>
                  <th>{{ $t('cdcDevicePage.computedColumnEnabled') }}</th>
                  <th>{{ $t('cdcDevicePage.computedColumnName') }}</th>
                  <th>{{ $t('cdcDevicePage.computedColumnType') }}</th>
                  <th>{{ $t('cdcDevicePage.computedColumnReading') }}</th>
                  <th>{{ $t('cdcDevicePage.computedColumnFormula') }}</th>
                  <th v-if="userMayEdit">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="sensor in computedSensors" :key="sensor.id">
                  <td v-if="userMayEdit"><span @click.stop="editComputed(sensor)"><font-awesome-icon icon="edit" /></span></td>
                  <td class="text-center">
                    <span v-if="sensor.enabled">{{ $t('cdcDevicePage.booleanTrue') }}</span>
                    <span v-else class="grey">{{ $t('cdcDevicePage.booleanFalse') }}</span>
                  </td>
                  <td>{{ sensor.name }}</td>
                  <td>{{ sensorTypeLabel(sensor.type) }}</td>
                  <td>{{ readingLabelFor(sensor) }}</td>
                  <td>{{ formulaFor(sensor) }}</td>
                  <td v-if="userMayEdit"><span @click.stop="askDeleteComputed(sensor)"><font-awesome-icon icon="trash" /></span></td>
                </tr>
              </tbody>
            </table>
            <button v-if="userMayEdit" type="button" class="btn btn-primary" @click="showAddComputedSensor">
              <font-awesome-icon icon="plus" /> &nbsp; {{ $t('cdcDevicePage.addComputedSensorButton') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <page-footer />

    <edit-device-modal @modalClosed="modalClosed" :selectedFacility="selectedFacility" :selectedCDCDevice="selectedDevice" :action="selectedAction" :modalOpened="modalTimestamp" />

    <add-virtual-sensor-modal @modalClosed="modalClosed" :parentFacility="selectedFacility" :modalOpened="modalTimestamp" />

    <edit-computed-sensor-modal @modalClosed="computedModalClosed" :parentFacility="selectedFacility" :selectedSensor="selectedComputedSensor" :candidateSensors="memberCandidates" :action="computedAction" :modalOpened="computedModalTimestamp" />

    <confirm-modal
      :title="confirmTitle"
      :text="confirmText"
      :confirmButtonLabel="$t('confirmModal.confirmButtonLabel')"
      :cancelButtonLabel="$t('confirmModal.cancelButtonLabel')"
      @confirm="confirmDeleteAction"
      @modalClosed="modalClosed"
    />
  </div>
</template>

<script>
import moment from 'moment'
import objectSorter from '@/utils/object-sorter'
import PageHeader from '@/components/PageHeader.vue'
import ErrorRow from '@/components/ErrorRow.vue'
import PageFooter from '@/components/PageFooter.vue'
import ConfirmModal from '@/modals/ConfirmModal.vue'
import PageViewMixin from '@/mixins/PageViewMixin.js'
import EditCDCDeviceModal from '@/modals/EditCDCDeviceModal.vue'
import AddVirtualSensorModal from '@/modals/AddVirtualSensorModal.vue'
import EditComputedSensorModal from '@/modals/EditComputedSensorModal.vue'
import cdcdeviceService from '@/services/cdcdevices'
import cdcsensorService from '@/services/cdcsensors'
import { isComputedSensor, parseMembers, formulaString, computedPair } from '@/utils/computed-sensors'

import { mapGetters } from 'vuex'

export default {
  name: 'CDCDevicesPage',
  mixins: [PageViewMixin],
  data () {
    return {
      facilityid: 0,
      selectedDevice: null,
      selectedFacility: { id: -1, organizationid: -1, name: '' },
      selectedAction: 'add',
      currentSortKey: 'apikey',
      currentSortOrder: 'asc',
      isLoading: false,
      // Berechnete Zähler: alle Sensoren der Facility (für Formel-Anzeige
      // und Member-Picker) plus Modal-/Confirm-Zustand
      facilitySensors: [],
      selectedComputedSensor: null,
      computedAction: 'add',
      computedModalTimestamp: '',
      confirmTarget: 'device',
      computedToDelete: null
    }
  },
  mounted () {
    this.clearError()
    this.facilityid = this.$route.params.facilityid
    if (this.facilityid > 0) {
      this.selectedFacility = { id: this.facilityid, organizationid: -1, name: '' }
      this.loadDevices(true)
    }
  },
  unmounted () {
    this.$store.dispatch('discardCDCDeviceData')
  },
  computed: {
    ...mapGetters([
      'client',
      'hasOrganizations',
      'organizationMap',
      'facilities',
      'hasFacilities',
      'facilityMap',
      'cdcdevices',
      'hasCdcdevices'
    ]),
    selectedDeviceId () {
      return this.selectedDevice ? this.selectedDevice.id : -1
    },
    emptyDevice () {
      return {
        id: -1,
        facilityid: this.selectedFacility.id,
        name: '',
        apikey: '',
        lastseen: new Date(),
        serialnumber: '',
        enabled: false,
        notificationstatus: false,
        status: 0,
        commandarguments: ''
      }
    },
    confirmDeleteText () {
      if (this.selectedDevice != null) {
        return this.$t('cdcDevicePage.confirmDeleteText', { apikey: this.selectedDevice.apikey })
      }
      return ''
    },
    confirmTitle () {
      return this.confirmTarget === 'computed'
        ? this.$t('cdcDevicePage.deleteComputedSensor')
        : this.$t('cdcDevicePage.deleteDevice')
    },
    confirmText () {
      if (this.confirmTarget === 'computed') {
        return this.computedToDelete ? this.$t('cdcDevicePage.confirmDeleteComputedText', { name: this.computedToDelete.name }) : ''
      }
      return this.confirmDeleteText
    },
    computedSensors () {
      return this.facilitySensors.filter(sensor => isComputedSensor(sensor))
    },
    memberCandidates () {
      return this.facilitySensors.filter(sensor => !isComputedSensor(sensor))
    },
    physicalDevices () {
      return this.cdcdevices.filter(device => !device.virtual)
    },
    hasPhysicalDevices () {
      return this.physicalDevices.length > 0
    },
    virtualDevice () {
      return this.cdcdevices.find(device => device.virtual) || null
    },
    sortedDevices () {
      return objectSorter.sortedArray(this.physicalDevices, this.currentSortOrder, this.currentSortKey)
    },
    organizationForFacility () {
      return this.selectedFacility.id > 0 && this.hasOrganizations ? this.organizationMap[this.selectedFacility.organizationid] : { name: '', code: '' }
    }
  },
  components: {
    'page-header': PageHeader,
    'error-row': ErrorRow,
    'page-footer': PageFooter,
    'edit-device-modal': EditCDCDeviceModal,
    'add-virtual-sensor-modal': AddVirtualSensorModal,
    'edit-computed-sensor-modal': EditComputedSensorModal,
    'confirm-modal': ConfirmModal
  },
  watch: {
    cdcdevices () {
      if (!this.running) {
        this.dataHaveBeenLoaded()
      }
    }
  },
  methods: {
    formatDate (date) {
      if (date) {
        return moment(String(date)).format('DD.MM.YYYY HH:mm')
      }
    },
    loadDevices (reset) {
      this.isLoading = true
      const payload = { facilityId: this.selectedFacility.id }
      this.$store.commit('clearError')
      this.$store.dispatch('getCDCDeviceData', payload)
      if (reset) {
        this.selectedDevice = this.emptyDevice
      }
    },
    backToFacilities () {
      this.$router.push({ name: 'facilities', params: { organizationid: this.selectedFacility.organizationid } })
    },
    isDeviceSelected (id) {
      return this.selectedDevice && id === this.selectedDevice.id
    },
    selectRow (device) {
      if (this.selectedDevice !== device) {
        this.selectedDevice = device
      } else {
        this.selectedDevice = this.emptyDevice
      }
    },
    showDetail (action) {
      if (action === 'add') {
        this.selectedDevice = this.emptyDevice
        this.selectedAction = 'add'
      } else {
        this.selectedAction = 'edit'
      }
      this.showModalView()
    },
    editDevice (device) {
      this.selectedDevice = device
      this.selectedAction = 'edit'
      this.showModalView()
    },
    showModalView () {
      this.openModal('#editCDCDeviceModal')
    },
    showAddVirtualSensor () {
      this.openModal('#addVirtualSensorModal')
    },
    // Override of the mixin's askDelete: the shared confirm modal serves
    // both device and computed-sensor deletion, so tag the target first.
    askDelete () {
      this.confirmTarget = 'device'
      this.openModal('#confirmModal')
    },
    showAddComputedSensor () {
      this.selectedComputedSensor = null
      this.computedAction = 'add'
      this.computedModalTimestamp = new Date().toISOString()
      this.openModal('#editComputedSensorModal')
    },
    editComputed (sensor) {
      this.selectedComputedSensor = sensor
      this.computedAction = 'edit'
      this.computedModalTimestamp = new Date().toISOString()
      this.openModal('#editComputedSensorModal')
    },
    askDeleteComputed (sensor) {
      this.computedToDelete = sensor
      this.confirmTarget = 'computed'
      this.openModal('#confirmModal')
    },
    computedModalClosed (anyChanges) {
      if (anyChanges) {
        this.loadDevices(false)
      }
    },
    confirmDeleteAction () {
      if (this.confirmTarget === 'computed') {
        if (!this.computedToDelete) return
        cdcsensorService.deleteCDCSensor(this.computedToDelete.id).then(() => {
          this.computedToDelete = null
          this.loadDevices(false)
        }).catch(error => {
          this.errorMessage = this.localizedBackendMessage(error.message)
        })
        return
      }
      this.confirmDelete()
    },
    confirmDelete () {
      cdcdeviceService.deleteCDCDevice(this.selectedDevice.id).then((deletedDevice) => {
        this.loadDevices(true)
      }).catch(error => {
        this.errorMessage = this.localizedBackendMessage(error.message)
      })
    },
    // Alle Sensoren der Facility laden (Service direkt, ohne den
    // Store-Sensorbestand der Sensorseite zu überschreiben).
    loadFacilitySensors () {
      const devices = this.cdcdevices || []
      const requests = devices.map(device =>
        cdcsensorService.getCDCSensorData(device.id).then(data => (data && data.cdcsensors) || []).catch(() => [])
      )
      Promise.all(requests).then(results => {
        this.facilitySensors = [].concat(...results)
      })
    },
    formulaFor (sensor) {
      const members = parseMembers(sensor.json)
      if (!members) return ''
      return formulaString(members, (id) => {
        const found = this.facilitySensors.find(candidate => Number(candidate.id) === Number(id))
        return found ? found.name : null
      }, (sp) => this.$t('systemPart.' + sp))
    },
    sensorTypeLabel (type) {
      return type >= 0 ? this.$t('sensorType.' + type) : '? ' + type
    },
    // "Gesamt Eingehend [Wh]" — the reading a computed sensor aggregates
    readingLabelFor (sensor) {
      const pair = computedPair(sensor)
      const unit = this.$t('physicalUnit.' + pair.valuetype)
      return this.$t('systemPart.' + pair.systempart) + ' ' + this.$t('valueType.' + pair.valuetype) + (unit ? ' [' + unit + ']' : '')
    },
    sort (key) {
      // reverse sort order?
      if (key === this.currentSortKey) {
        this.currentSortOrder = this.currentSortOrder === 'asc' ? 'desc' : 'asc'
      }
      this.currentSortKey = key
    },
    reloadData () {
      this.loadDevices(false)
    },
    gotoSensors (device) {
      this.$router.push({ name: 'cdcsensors', params: { cdcdeviceid: device.id } })
    },
    dataHaveBeenLoaded () {
      if (this.facilityid > 0 && this.hasFacilities) {
        this.selectedFacility = this.facilityMap[this.facilityid]
      }
      this.loadFacilitySensors()
    },
    showData (device) {
      // this.testDataUpload(device) // FIXME!!!
      // this.testSensorConfigUpload(device)
      this.$router.push({ name: 'cdcdatavalues', params: { cdcdeviceid: device.id, from: 0, until: 0 } })
    },
    testDataUpload (device) {
      const payload = {
        key: device.apikey,
        data: [
          {
            rts: 1623064315,
            sts: 1623064315,
            pkg: 0,
            pk: 1,
            vs: [
              { v: 1.1238e8, f: -1, t: 9, s: 4 },
              { v: 1.1238e8, f: -1, t: 16, s: 18 },
              { v: 0, f: -1, t: 9, s: 19 },
              { v: 12, f: 0, t: 3, s: 4 }
            ]
          },
          {
            rts: 1623064260,
            sts: 2616424,
            pkg: 3044,
            pk: 1,
            vs: [
              { v: 5325, f: -2, t: 16, s: 18 },
              { v: 3717, f: -3, t: 1, s: 10 }
            ]
          }
        ]
      }
      cdcdeviceService.cdcDataUpload(payload).then((answer) => {
        // console.log('testDataUpload answer = ')
        // console.log(answer)
        this.loadDevices(true)
      }).catch(error => {
        this.errorMessage = this.localizedBackendMessage(error.message)
      })
    },
    testSensorConfigUpload (device) {
      const payload = {
        key: device.apikey,
        sensors: [
          {
            name: 'Kühlschrank',
            type: 4,
            protocol: 1,
            connector: 5,
            mainURI: 'D0D1D2D3D402',
            statusURIFragment: '',
            valueURIFragment: '',
            loginURIFragment: '',
            interval: 0,
            username: '',
            password: '',
            authMethod: 0,
            key: '',
            directReading: 0,
            enabled: 1,
            pk: 1,
            uuid: 'c2231cbc-c4b0-421f-a10c-804ac3cb3042',
            json: '{"model":"CS-T_Lv1"}',
            factor: -2
          },
          {
            name: 'CS-IRW',
            type: 2,
            protocol: 8,
            connector: 0,
            mainURI: '',
            statusURIFragment: '',
            valueURIFragment: '',
            loginURIFragment: '',
            interval: 0,
            username: '',
            password: '',
            authMethod: 0,
            key: '',
            directReading: 0,
            enabled: 1,
            pk: 2,
            uuid: '78c62c14-194b-4643-bedc-c0d00fc31154',
            json: '{"model":"CS-IRWv1","th":2000,"dl":500,"factor":-4}',
            factor: 0
          }
        ]
      }
      cdcdeviceService.cdcSensorConfigUpload(payload).then((answer) => {
        // console.log('testSensorConfigUpload answer = ')
        // console.log(answer)
        this.loadDevices(true)
      }).catch(error => {
        this.errorMessage = this.localizedBackendMessage(error.message)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.content-container {
  padding: 0 35px;

  h2 {
    font-size: 18px;
    margin-bottom: 15px;
    font-weight: 400;
  }

  .magazine-list-section {
    margin: 30px 10px;
  }

  .representation-hierarchy {
    margin-top: 30px;
  }

  table.auto-col {
    table-layout: auto;
  }

  .grey {
    color: #808080;
  }

  .blue-text {
    color: #0069d9;
    font-weight: 700;
  }
  .toggle-icon {
    margin-left: 20px;
  }
}
</style>
